import { getInterviewDurationEstimate } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { Link } from "@repo/ui/Link/index";
import { Modal } from "@repo/ui/Modal/modal";
import { SmallTabs } from "@repo/ui/Navigation/smallTabs";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconCircleCheck } from "@tabler/icons-react";
import copy from "copy-to-clipboard";
import { type FC, useState } from "react";
import { Heading } from "react-aria-components";
import { copyRichText } from "../../helpers/utilities.js";

interface InterviewLinkTipsModalProps {
  isOpen: boolean;
  incentiveQuantity: number | null;
  link: string;
  onClose: () => void;
  numQuestions: number;
}

export const InterviewLinkTipsModal: FC<InterviewLinkTipsModalProps> = ({
  isOpen,
  incentiveQuantity: incentiveAmount,
  link,
  onClose,
  numQuestions,
}) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const incentiveTip = [
    "Emphasize the gift card in your subject line and main message to dramatically increase completion rates.",
  ];
  const otherTips = [
    "Send your interview link to at least 50-100 participants to get a meaningful number of interviews and understand your conversion rates.",
    "To get ongoing interviews, add your link to automated touchpoints (like a welcome email or post cancellation email), rather than one-off manual email blasts.",
  ];

  const tips = incentiveAmount ? [...incentiveTip, ...otherTips] : otherTips;

  const useEmailVariant = selectedTabIndex === 0;
  const template = getTemplate(
    link,
    incentiveAmount,
    useEmailVariant,
    numQuestions,
  );

  return (
    <Modal isOpen={isOpen} onCancel={onClose} size={Size.LARGE}>
      <div className="flex flex-col items-center">
        <div className="flex flex-row justify-between">
          <Heading
            slot="title"
            className="text-xl leading-6 font-semibold text-foreground"
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <IconCircleCheck
                className="h-6 w-6 text-success"
                aria-hidden="true"
              />
            </div>
            <div>{"Interview link copied"}</div>
          </Heading>
        </div>
        <div className="mt-6 flex flex-row space-x-4">
          <div className="flex-1 text-sm">
            <div className="mb-2">
              <Label size={Size.SMALL}>
                Tips for sharing your link with participants
              </Label>
            </div>
            {tips.map((tip, index) => {
              return (
                <div
                  className="mb-4 flex items-start justify-center space-x-3"
                  key={`tip-${index}`}
                >
                  <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-blue-600 text-sm text-blue-600">
                    {index + 1}
                  </div>
                  <span>{tip}</span>
                </div>
              );
            })}
            <div className="text-sm">
              <Link href="https://help.alpharun.com/en/articles/687232">
                Check out the guide
              </Link>
              {" for more examples, and reach out to "}
              <Link href="mailto:support@alpharun.com">
                support@alpharun.com
              </Link>
              {" with any questions."}
            </div>
          </div>
          <div className="flex flex-1 flex-col space-y-4">
            <Label size={Size.SMALL}>Sharing templates</Label>
            <div>
              <SmallTabs
                tabs={["Email", "Chat, SMS, Plaintext"]}
                selectedTabIndex={selectedTabIndex}
                onSelectTabIndex={setSelectedTabIndex}
              />
            </div>
            <div className="mt-4 flex w-full cursor-text space-x-2 rounded-md border border-solid border-border-strong bg-slate-50 px-2 py-2 text-xs">
              {template.react}
            </div>
            <div>
              <Button
                variant={ButtonVariant.Secondary}
                onClick={async () => {
                  if (template.richText)
                    await copyRichText(template.richText, template.plainText);
                  else {
                    copy(template.plainText);
                  }

                  showToast({
                    title: `Template copied`,
                    variant: ToastVariant.Success,
                  });
                }}
              >
                Copy template
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-center sm:mt-6">
          <Button variant={ButtonVariant.Primary} onClick={onClose}>
            Got it
          </Button>
        </div>
      </div>
    </Modal>
  );
};

function renderTemplateRichText(elements: {
  subject: string;
  greeting: string;
  introLine: string;
  durationEstimate: string;
  link: string;
  giftCardText: string;
}): string {
  const container = document.createElement("div");

  const subjectNode = document.createTextNode(elements.subject);
  container.appendChild(subjectNode);
  container.appendChild(document.createElement("br"));

  const greetingNode = document.createTextNode(`${elements.greeting},`);
  container.appendChild(greetingNode);
  container.appendChild(document.createElement("br"));
  container.appendChild(document.createElement("br"));

  const introNode = document.createTextNode(capitalize(elements.introLine));
  container.appendChild(introNode);

  const surveyTextNode = document.createTextNode(
    `${elements.durationEstimate} survey: `,
  );
  container.appendChild(surveyTextNode);

  const linkElement = document.createElement("a");
  linkElement.href = elements.link;
  linkElement.textContent = elements.link;
  container.appendChild(linkElement);

  container.appendChild(document.createElement("br"));
  container.appendChild(document.createElement("br"));

  if (elements.giftCardText) {
    const giftCardNode = document.createTextNode(elements.giftCardText);
    container.appendChild(giftCardNode);
  }

  return container.outerHTML;
}

function getTemplate(
  link: string,
  incentiveAmount: number | null,
  useEmailVariant: boolean,
  numQuestions: number,
) {
  const abbreviatedDurationEstimate = getInterviewDurationEstimate(
    numQuestions,
    {
      shouldAbbreviate: true,
    },
  );

  const subject = `Subject: ${abbreviatedDurationEstimate} research survey with gift card`;
  const greeting = "Hi {{first_name}}";
  const introLine = `we'd love to get your feedback via a `;

  const durationEstimate = getInterviewDurationEstimate(numQuestions, {
    shouldUseSingularPhrasing: true,
  });

  const surveyLinkLinePlaintext = `${durationEstimate} survey: ${link} .`;
  const surveyLinkLineReact = (
    <span>
      {`${durationEstimate} survey: `}
      <Link href={link}>{link}</Link>
    </span>
  );
  const giftCardText = incentiveAmount
    ? `As a small thank you for your time, you'll receive a $${incentiveAmount} gift card
(your choice of Amazon, Visa, charity donation, and more) after completing the survey.`
    : "";

  if (useEmailVariant)
    return {
      plainText: `${subject}\n\n${greeting},\n\n${capitalize(
        introLine,
      )}${surveyLinkLinePlaintext}\n\n${giftCardText}`,
      richText: renderTemplateRichText({
        subject,
        greeting,
        introLine,
        durationEstimate,
        link,
        giftCardText,
      }),
      react: (
        <div>
          {subject}
          <br />
          {greeting}
          {","}
          <br />
          <br />
          {capitalize(introLine)}
          {surveyLinkLineReact}
          {incentiveAmount ? (
            <>
              <br />
              <br />
              {giftCardText}
            </>
          ) : null}
        </div>
      ),
    };

  // Plaintext variant with no subject
  return {
    plainText: `${greeting} - ${introLine}${surveyLinkLinePlaintext} ${giftCardText}`,
    react: (
      <div>
        {greeting}
        {" - "}
        {introLine}
        {surveyLinkLineReact} {giftCardText}
      </div>
    ),
  };
}
